<template>
  <div>
    <div class="adm-card">
      <div class="adm-card-head has-tabs">
        <el-tabs v-model="activeType" @tab-click="onTypeChange">
          <el-tab-pane
            v-for="t in types"
            :key="t.value"
            :label="t.label"
            :name="t.value"
          />
        </el-tabs>
      </div>

      <div class="adm-filter">
        <el-input
          v-model="query.keyword"
          class="adm-w240"
          name="keyword"
          autocomplete="off"
          prefix-icon="el-icon-search"
          placeholder="搜索内容"
          clearable
          @keyup.enter.native="onSearch"
        />
        <el-input
          v-model="query.userId"
          class="adm-w180"
          name="userId"
          autocomplete="off"
          placeholder="按用户 ID 过滤"
          clearable
          @keyup.enter.native="onSearch"
        />
        <el-button type="primary" icon="el-icon-search" :loading="loading" @click="onSearch">
          查询
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="onReset">重置</el-button>
      </div>

      <el-table v-loading="loading" :data="rows" border>
        <el-table-column prop="id" label="内容 ID" width="180">
          <template slot-scope="{ row }">
            <span class="adm-code">{{ row.id }}</span>
          </template>
        </el-table-column>
        <el-table-column label="归属用户" min-width="160">
          <template slot-scope="{ row }">
            <span>{{ row.ownerNickname || '-' }}</span>
            <span class="adm-sub">（{{ row.ownerId }}）</span>
          </template>
        </el-table-column>
        <el-table-column prop="summary" label="内容" min-width="240" show-overflow-tooltip />
        <el-table-column prop="extra" label="附加信息" width="180" show-overflow-tooltip />
        <el-table-column v-if="isShareType" label="分享类型" width="110">
          <template slot-scope="{ row }">
            <el-tag :type="row.source === 'GRANT' ? 'warning' : 'info'" size="mini">
              {{ shareTypeText(row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="150">
          <template slot-scope="{ row }">{{ formatTime(row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag :type="statusTagType(row)" size="mini">{{ statusText(row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template slot-scope="{ row }">
            <div class="adm-op-actions">
              <el-link
                v-if="canEdit"
                type="primary"
                :underline="false"
                @click="openEdit(row)"
              >编辑</el-link>
              <el-link
                v-if="!row.isDeleted"
                type="danger"
                :underline="false"
                @click="openAction(row, 'hide')"
              >下架</el-link>
              <el-link
                v-else-if="row.restorable"
                type="primary"
                :underline="false"
                @click="openAction(row, 'restore')"
              >恢复</el-link>
              <span v-else class="adm-sub">-</span>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <AdmPager
        :total="total"
        :page="query.page"
        :size="query.size"
        @page-change="onPageChange"
        @size-change="onSizeChange"
      />
    </div>

    <el-dialog
      :title="actionMode === 'hide' ? '下架内容' : '恢复内容'"
      :visible.sync="actionVisible"
      width="520px"
      append-to-body
    >
      <div class="adm-desc">
        <div class="adm-desc-item is-full">
          <span class="adm-desc-label">内容</span>
          <span class="adm-desc-value">{{ actionRow && actionRow.summary }}</span>
        </div>
        <div v-if="actionRow && actionRow.source" class="adm-desc-item is-full">
          <span class="adm-desc-label">分享类型</span>
          <span class="adm-desc-value">{{ shareTypeText(actionRow) }}</span>
        </div>
        <div class="adm-desc-item is-full">
          <span class="adm-desc-label">归属用户</span>
          <span class="adm-desc-value">
            {{ actionRow && actionRow.ownerNickname }}（{{ actionRow && actionRow.ownerId }}）
          </span>
        </div>
      </div>

      <div class="adm-field-label">
        {{ actionMode === 'hide' ? '下架原因' : '恢复原因' }}
      </div>
      <el-input
        v-model="actionReason"
        name="reason"
        autocomplete="off"
        type="textarea"
        :rows="3"
        maxlength="200"
        show-word-limit
        :placeholder="actionMode === 'hide' ? '请填写下架原因（必填，会留痕）' : '请填写恢复原因（必填，会留痕）'"
      />
      <div slot="footer">
        <el-button size="small" @click="actionVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="actionLoading" @click="submitAction">
          确定
        </el-button>
      </div>
    </el-dialog>

    <el-dialog title="编辑内容" :visible.sync="editVisible" width="560px" append-to-body>
      <div class="adm-desc">
        <div class="adm-desc-item is-full">
          <span class="adm-desc-label">归属用户</span>
          <span class="adm-desc-value">
            {{ editRow && editRow.ownerNickname || '-' }}（{{ editRow && editRow.ownerId }}）
          </span>
        </div>
      </div>

      <div class="adm-field-label">内容正文（最多 {{ editMaxLength }} 字）</div>
      <el-input
        v-model="editContent"
        name="content"
        autocomplete="off"
        type="textarea"
        :rows="4"
        :maxlength="editMaxLength"
        show-word-limit
        placeholder="请输入修改后的内容"
      />
      <div class="adm-inline-hint">
        修改会留痕（记录改前 / 改后快照），但不会改变上下架状态。
      </div>

      <div slot="footer">
        <el-button size="small" @click="editVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="editSaving" @click="submitEdit">
          保存
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { fetchAuditContents, hideContent, restoreContent, updateContent, isOk } from '@/api/admin'
import AdmPager from '@/components/admin/AdmPager.vue'

const TYPES = [
  { value: 'THOUGHT', label: '随想' },
  { value: 'SHARED_COMMENT', label: '分享评论' },
  { value: 'WORKSPACE_SHARE', label: '工作区分享' },
  { value: 'PROJECT_SHARE', label: '工作表分享' }
]

/**
 * 只有「用户书写了正文」的类型能改内容：
 * 随想正文上限 255、评论上限 100（与 DDL 一致）。分享记录只有可见性这类
 * 结构化字段，没有正文，因此不提供编辑入口。
 */
const EDITABLE_MAX_LENGTH = { THOUGHT: 255, SHARED_COMMENT: 100 }

/** 分享类标签页（这两类才有「分享类型」列） */
const SHARE_TYPES = ['WORKSPACE_SHARE', 'PROJECT_SHARE']

export default {
  name: 'AuditPanel',
  components: { AdmPager },
  data() {
    return {
      types: TYPES,
      activeType: 'THOUGHT',
      query: { keyword: '', userId: '', page: 1, size: 20 },
      rows: [],
      total: 0,
      loading: false,
      actionVisible: false,
      actionLoading: false,
      actionMode: 'hide',
      actionRow: null,
      actionReason: '',
      editVisible: false,
      editSaving: false,
      editRow: null,
      editContent: ''
    }
  },
  computed: {
    /** 当前类型是否有可编辑正文 */
    canEdit() {
      return !!EDITABLE_MAX_LENGTH[this.activeType]
    },
    editMaxLength() {
      return EDITABLE_MAX_LENGTH[this.activeType] || 255
    },
    /** 分享类：可见性分享与好友授权两类记录合并在同一张表里，需要多一列说明来源 */
    isShareType() {
      return SHARE_TYPES.includes(this.activeType)
    }
  },
  created() {
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      fetchAuditContents({ type: this.activeType, ...this.query })
        .then(res => {
          if (!isOk(res)) {
            throw new Error(res && res.message ? res.message : '查询失败')
          }
          this.rows = res.data.list || []
          this.total = Number(res.data.total || 0)
        })
        .catch(err => {
          this.$message.error(err.message || '查询失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    onTypeChange() {
      this.query.page = 1
      // 切类型时把编辑弹窗收起来：不同类型的正文字段上限不同，留着容易改错
      this.editVisible = false
      this.load()
    },
    onSearch() {
      this.query.page = 1
      this.load()
    },
    onReset() {
      this.query = { keyword: '', userId: '', page: 1, size: 20 }
      this.load()
    },
    onPageChange(page) {
      this.query.page = page
      this.load()
    },
    onSizeChange(size) {
      this.query.size = size
      this.query.page = 1
      this.load()
    },
    openAction(row, mode) {
      this.actionRow = row
      this.actionMode = mode
      this.actionReason = ''
      this.actionVisible = true
    },
    submitAction() {
      if (this.actionLoading) return
      const reason = this.actionReason.trim()
      if (!reason) {
        this.$message.warning('请填写原因')
        return
      }
      this.actionLoading = true
      const payload = {
        resourceType: this.activeType,
        resourceId: this.actionRow.id,
        reason
      }
      const call = this.actionMode === 'hide' ? hideContent : restoreContent
      call(payload)
        .then(res => {
          if (!isOk(res)) {
            throw new Error(res && res.message ? res.message : '操作失败')
          }
          this.$message.success(this.actionMode === 'hide' ? '已下架' : '已恢复')
          this.actionVisible = false
          this.load()
        })
        .catch(err => {
          this.$message.error(err.message || '操作失败')
        })
        .finally(() => {
          this.actionLoading = false
        })
    },
    openEdit(row) {
      this.editRow = row
      this.editContent = row.summary || ''
      this.editVisible = true
    },
    submitEdit() {
      if (this.editSaving) return
      const content = this.editContent.trim()
      if (!content) {
        this.$message.warning('内容不能为空')
        return
      }
      this.editSaving = true
      updateContent({
        resourceType: this.activeType,
        resourceId: this.editRow.id,
        content
      })
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '保存失败')
          }
          this.$message.success('已保存')
          this.editVisible = false
          this.load()
        })
        .catch(err => {
          this.$message.error((err && err.message) || '保存失败')
        })
        .finally(() => {
          this.editSaving = false
        })
    },
    statusText(row) {
      if (row.hidden) return '已下架'
      if (row.isDeleted) return '已删除'
      return '正常'
    },
    /** 分享来源：GRANT=开放给指定好友（share_grants），其余=按可见性分享（project_shares） */
    shareTypeText(row) {
      if (row && row.source === 'GRANT') return '好友授权'
      if (row && row.source) return '可见性分享'
      return '-'
    },
    statusTagType(row) {
      if (row.hidden) return 'danger'
      if (row.isDeleted) return 'info'
      return 'success'
    },
    formatTime(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').slice(0, 16)
    }
  }
}
</script>

<style scoped>
/* 卡片 / 筛选栏 / 分页样式统一来自 @/styles/admin.css */
.adm-w240 {
  width: 240px;
}

.adm-w180 {
  width: 180px;
}

/* 选项卡直接当卡片头用 */
.adm-card-head.has-tabs {
  padding: 0 20px;
  min-height: 0;
}

.adm-card-head.has-tabs .el-tabs {
  width: 100%;
}

.adm-field-label {
  margin: 16px 0 8px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
}
</style>
